import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MerchantPayoutDashboard.css';
import { FiEye, FiEyeOff } from "react-icons/fi";
import useFocusTrap from '../hooks/useFocusTrap';
import usePagination from '../hooks/usePagination';
import Pagination from './Pagination';
import BulkPayoutCreator from './BulkPayoutCreator';


// Decode JWT payload without verification (for expiry check only)
const decodeJwtPayload = (token) => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch { return null; }
};

const isPayoutTokenValid = (token) => {
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  if (!payload) return false;
  // Check standard JWT expiry
  if (payload.exp && Date.now() / 1000 > payload.exp) return false;
  return true;
};

const MerchantPayoutDashboard = () => {
  // Get merchant-specific storage key to isolate sessions per merchant
  const getPayoutTokenKey = () => {
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      return `payoutToken_${userData.merchantId || 'unknown'}`;
    } catch { return 'payoutToken_unknown'; }
  };

  // Payout session state
  const [payoutToken, setPayoutToken] = useState(null);
  const [payoutLoginForm, setPayoutLoginForm] = useState({ username: '', password: '', payoutKey: '' });
  const [payoutLoginError, setPayoutLoginError] = useState('');
  const [payoutLoginLoading, setPayoutLoginLoading] = useState(false);

  const [showPayoutPassword, setShowPayoutPassword] = useState(false);
  const [showPayoutKey, setShowPayoutKey] = useState(false);

  const [payouts, setPayouts] = useState([]);
  const payoutPagination = usePagination(payouts, 10);
  const [totalPayoutCount, setTotalPayoutCount] = useState(0);
  const [stats, setStats] = useState({
    totalPayouts: 0,
    successfulPayouts: 0,
    pendingPayouts: 0,
    totalAmount: 0,
    successRate: 0
  });
  const [filters, setFilters] = useState({
    status: '',
    dateFrom: '',
    dateTo: '',
    minAmount: '',
    maxAmount: ''
  });
  const [loading, setLoading] = useState(true);
  const [showBulkCreate, setShowBulkCreate] = useState(false);
  const [notification, setNotification] = useState(null);
  const [selectedPayoutId, setSelectedPayoutId] = useState(null);
  const [payoutDetails, setPayoutDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Bank Services state
  const [bankServiceResult, setBankServiceResult] = useState(null);
  const [bankServiceLoading, setBankServiceLoading] = useState(false);
  const [inquiryUniqueId, setInquiryUniqueId] = useState('');
  const [statementFrom, setStatementFrom] = useState('');
  const [statementTo, setStatementTo] = useState('');

  useEffect(() => {
    // Check for existing payout session on mount — merchant-specific key
    const storedToken = sessionStorage.getItem(getPayoutTokenKey());
    if (isPayoutTokenValid(storedToken)) {
      setPayoutToken(storedToken);
    }
  }, []);

  useEffect(() => {
    if (payoutToken) {
      fetchPayouts();
      // Only fetch stats when no filters active - stats API always returns unfiltered totals
      const hasActiveFilters = filters.status || filters.dateFrom || filters.dateTo || filters.minAmount || filters.maxAmount;
      if (!hasActiveFilters) {
        fetchStats();
      }
    }
  }, [filters, payoutToken]);

  const fetchPayouts = async () => {
    if (!payoutToken) return;
    // Block fetch if date range is invalid
    if (filters.dateFrom && filters.dateTo && filters.dateTo < filters.dateFrom) return;
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.minAmount) params.append('minAmount', filters.minAmount);
      if (filters.maxAmount) params.append('maxAmount', filters.maxAmount);
      params.append('size', '500');  // fetch all records
      params.append('page', '0');
      params.append('sort', 'createdAt,desc');

      const response = await axios.get(`/api/v1/payouts?${params.toString()}`, {
        headers: { Authorization: `Bearer ${payoutToken}` },
        _skipAuthInterceptor: true
      });

      // Backend returns Page<PayoutSummary> directly
      if (response.data && response.data.content) {
        const content = response.data.content || [];
        setPayouts(content);
        setTotalPayoutCount(response.data.totalElements || content.length);

        // When filters are active, derive stats from the response
        const hasActiveFilters = filters.status || filters.dateFrom || filters.dateTo || filters.minAmount || filters.maxAmount;
        if (hasActiveFilters) {
          // We only have current page — use totalElements for total, derive status counts from page
          const successful = content.filter(p => p.status === 'SUCCESS').length;
          const pending = content.filter(p =>
            ['PENDING_APPROVAL','APPROVED','INITIATED','PROCESSING'].includes(p.status)).length;
          const totalAmt = content.reduce((sum, p) => sum + (p.amount || 0), 0);
          const total = response.data.totalElements || content.length;
          setStats({
            totalPayouts: total,
            successfulPayouts: successful,
            pendingPayouts: pending,
            totalAmount: totalAmt,
            successRate: content.length > 0 ? (successful * 100 / content.length) : 0
          });
        }
      } else if (Array.isArray(response.data)) {
        setPayouts(response.data);
        setTotalPayoutCount(response.data.length);
      } else {
        console.warn('Unexpected response format:', response.data);
        setPayouts([]);
        setTotalPayoutCount(0);
      }
    } catch (error) {
      console.error('Error fetching payouts:', error);
      if (error.response?.status === 401) {
        window.location.href = '/merchant/login';
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    if (!payoutToken) return;
    try {
      console.log('[STATS] Fetching payout stats...');

      console.log('[STATS] Calling /api/v1/payouts/stats');
      const response = await axios.get('/api/v1/payouts/stats', {
        headers: { Authorization: `Bearer ${payoutToken}` },
        _skipAuthInterceptor: true
      });

      console.log('[STATS] Response received:', response.data);
      console.log('[STATS] Total Payouts:', response.data.totalPayouts);
      console.log('[STATS] Total Amount:', response.data.totalAmount);
      console.log('[STATS] Success Rate:', response.data.successRate);

      // Backend returns PayoutStats directly
      if (response.data) {
        const newStats = {
          totalPayouts: response.data.totalPayouts || 0,
          successfulPayouts: response.data.successfulPayouts || 0,
          pendingPayouts: response.data.pendingPayouts || 0,
          totalAmount: response.data.totalAmount || 0,
          successRate: response.data.successRate || 0
        };
        console.log('[STATS] Setting stats:', newStats);
        console.log('[STATS] New Total Amount:', newStats.totalAmount);
        console.log('[STATS] New Success Rate:', newStats.successRate);
        setStats(newStats);
      } else {
        console.warn('[STATS] No data in response');
      }
    } catch (error) {
      console.error('[STATS] Error fetching stats:', error);
      console.error('[STATS] Error details:', error.response?.data);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusClass = 
      status === 'SUCCESS' ? 'status-success' :
      status === 'PENDING_APPROVAL' ? 'status-warning' :
      status === 'PENDING_BANK_APPROVAL' ? 'status-warning' :
      status === 'APPROVED' || status === 'INITIATED' || status === 'PROCESSING' ? 'status-pending' :
      status === 'FAILED' ? 'status-failed' :
      status === 'REJECTED' ? 'status-danger' : 'status-secondary';

    const statusLabel = {
      'PENDING_APPROVAL': 'Pending Approval',
      'APPROVED': 'Approved',
      'INITIATED': 'Initiated',
      'PROCESSING': 'Awaiting CIB Portal Approval',
      'SUCCESS': 'Success',
      'FAILED': 'Failed',
      'REJECTED': 'Rejected',
      'PENDING_VERIFICATION': 'Pending Verification',
    }[status] || status;
    
    return <span className={`status-badge ${statusClass}`}>{statusLabel}</span>;
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      dateFrom: '',
      dateTo: '',
      minAmount: '',
      maxAmount: ''
    });
  };

  // Bank Service Functions
  const checkBalance = async () => {
    setBankServiceLoading(true);
    setBankServiceResult(null);
    try {
      const res = await axios.get('/api/v1/payouts/balance', {
        headers: { Authorization: `Bearer ${sessionStorage.getItem(getPayoutTokenKey())}` }, _skipAuthInterceptor: true
      });
      setBankServiceResult({ type: 'balance', data: res.data, error: null });
    } catch (e) {
      setBankServiceResult({ type: 'balance', data: null, error: e.response?.data?.message || e.message });
    } finally { setBankServiceLoading(false); }
  };

  const getAccountStatement = async () => {
    if (!statementFrom || !statementTo) { setBankServiceResult({ type: 'statement', data: null, error: 'Please select both From and To dates.' }); return; }
    if (statementTo < statementFrom) { setBankServiceResult({ type: 'statement', data: null, error: 'To date cannot be earlier than From date.' }); return; }
    setBankServiceLoading(true);
    setBankServiceResult(null);
    try {
      const res = await axios.get(`/api/v1/payouts/statement?fromDate=${statementFrom}&toDate=${statementTo}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem(getPayoutTokenKey())}` }, _skipAuthInterceptor: true
      });
      setBankServiceResult({ type: 'statement', data: res.data, error: null });
    } catch (e) {
      setBankServiceResult({ type: 'statement', data: null, error: e.response?.data?.message || e.message });
    } finally { setBankServiceLoading(false); }
  };

  const transactionInquiry = async () => {
    if (!inquiryUniqueId.trim()) { alert('Please enter a UNIQUEID'); return; }
    setBankServiceLoading(true);
    setBankServiceResult(null);
    try {
      const res = await axios.get(`/api/v1/payouts/inquiry/${inquiryUniqueId.trim()}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem(getPayoutTokenKey())}` }, _skipAuthInterceptor: true
      });
      setBankServiceResult({ type: 'inquiry', data: res.data, error: null });
    } catch (e) {
      setBankServiceResult({ type: 'inquiry', data: null, error: e.response?.data?.message || e.message });
    } finally { setBankServiceLoading(false); }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Payout login handler
  const handlePayoutLogin = async (e) => {
    e.preventDefault();
    setPayoutLoginError('');

    // Client-side validation — show one clear message instead of browser tooltip
    if (!payoutLoginForm.username.trim() || !payoutLoginForm.password.trim() || !payoutLoginForm.payoutKey.trim()) {
      setPayoutLoginError('Please fill in all fields (Username, Password, and Payout Key)');
      return;
    }

    setPayoutLoginLoading(true);
    try {
      // OLD: fetched merchantId from localStorage (tamper-able)
      // let merchantId = null;
      // try {
      //   const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      //   merchantId = userData.merchantId || null;
      // } catch (_) {}
      // const loginPayload = { ...payoutLoginForm };
      // if (merchantId) loginPayload.merchantId = merchantId;

      // NEW: send the merchant's signed dashboard JWT as X-Merchant-Token header
      // Backend extracts merchantId from it (tamper-proof, signed by server)
      const dashboardToken = localStorage.getItem('token');
      const loginPayload = { ...payoutLoginForm };

      const headers = { 'Content-Type': 'application/json' };
      if (dashboardToken) {
        headers['X-Merchant-Token'] = dashboardToken;
      }

      const res = await axios.post('/api/auth/payout-login', loginPayload, { 
        headers,
        withCredentials: true 
      });
      if (res.data.success && res.data.data?.token) {
        const token = res.data.data.token;
        localStorage.setItem('payoutToken', token);  // keep for cross-tab if needed
        sessionStorage.setItem(getPayoutTokenKey(), token);
        setPayoutToken(token);
        setPayoutLoginForm({ username: '', password: '', payoutKey: '' });
      } else {
        setPayoutLoginError(res.data.message || 'Login failed');
      }
    } catch (err) {
      setPayoutLoginError(err.response?.data?.message || err.response?.data?.error || 'Login failed');
    } finally {
      setPayoutLoginLoading(false);
    }
  };

  // Payout logout handler
  const handlePayoutLogout = () => {
    localStorage.removeItem('payoutToken');
    sessionStorage.removeItem(getPayoutTokenKey());
    setPayoutToken(null);
    setPayouts([]);
    setStats({ totalPayouts: 0, successfulPayouts: 0, pendingPayouts: 0, totalAmount: 0, successRate: 0 });
  };

  const fetchPayoutDetails = async (payoutId) => {
    setDetailsLoading(true);
    setSelectedPayoutId(payoutId);
    try {
      const response = await axios.get(`/api/v1/payouts/${payoutId}`, {
        headers: payoutToken ? { Authorization: `Bearer ${payoutToken}` } : {},
        _skipAuthInterceptor: true
      });

      // Backend returns PayoutDetails directly
      if (response.data) {
        setPayoutDetails(response.data);
      }
    } catch (error) {
      console.error('Error fetching payout details:', error);
      showNotification(error.response?.data?.message || 'Failed to load payout details', 'error');
      setSelectedPayoutId(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetailsModal = () => {
    setSelectedPayoutId(null);
    setPayoutDetails(null);
  };

  if (loading && payoutToken) {
    return <div className="payout-dashboard-loading" role="status" aria-live="polite">Loading payouts...</div>;
  }

  // Show inline login form if no valid payout session
  if (!payoutToken) {
    return (
      <div className="payout-dashboard" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '60vh' }}>
        <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '12px', padding: '40px', width: '100%', maxWidth: '420px', marginTop: '24px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
          <h1 style={{ textAlign: 'center', marginTop: 0, marginBottom: '8px' }}>💸 Payout Access</h1>
          <p style={{ textAlign: 'center', color: '#666', fontSize: '14px', marginBottom: '24px' }}>
            Enter your payout credentials to access the payout dashboard.
          </p>
          <form onSubmit={handlePayoutLogin} autoComplete="off">
            <div style={{ marginBottom: '16px' }}>
              <label htmlFor="payout-username" style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Username</label>
              <input id="payout-username" type="text" value={payoutLoginForm.username}
                onChange={e => setPayoutLoginForm({ ...payoutLoginForm, username: e.target.value })}
                placeholder="Payout username" required aria-required="true"
                autoComplete="off"
                style={{ width: '100%', padding: '10px 40px 10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label htmlFor="payout-password" style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Password</label>
               <div className="password-wrapper">

               <input id="payout-password" 
               type={showPayoutPassword ? "text" : "password"}
               value={payoutLoginForm.password}
                onChange={e => setPayoutLoginForm({ ...payoutLoginForm, password: e.target.value })}
                placeholder="Password" required aria-required="true"
                autoComplete="new-password"
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
              <span
              onClick={() => setShowPayoutPassword(!showPayoutPassword)}>
             {showPayoutPassword ? <FiEye size={18} /> : <FiEyeOff size={18} />}
              </span>
             </div>
             </div>


            <div style={{ marginBottom: '20px' }}>
              <label 
              htmlFor="payout-key" style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Payout Key</label>
              <div className="password-wrapper">
              <input id="payout-key" 
              type={showPayoutKey ? "text" : "password"} 
              value={payoutLoginForm.payoutKey}
              onChange={e => setPayoutLoginForm({ ...payoutLoginForm, payoutKey: e.target.value })}
              placeholder="Payout access key" required aria-required="true"
              autoComplete="new-password"
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
           <span onClick={() => setShowPayoutKey(!showPayoutKey)}>
               {showPayoutKey ? <FiEye size={18} /> : <FiEyeOff size={18} />}
           </span>
           </div>
           </div>
            {payoutLoginError && (
              <div style={{ background: '#f8d7da', color: '#721c24', padding: '10px 14px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px' }}>
                ❌ {payoutLoginError}
              </div>
            )}
            <button type="submit" disabled={payoutLoginLoading}
              style={{ width: '100%', padding: '12px', background: '#007a75', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>
              {payoutLoginLoading ? 'Logging in...' : 'Login to Payout Dashboard'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="payout-dashboard">
      {/* Notification Toast */}
      {notification && (
        <div className={`notification-toast ${notification.type}`}>
          {notification.type === 'success' ? '✅ ' : '❌ '}
          {notification.message}
        </div>
      )}

      <div className="payout-dashboard-header">
        <h1>💸 Payout Management</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button 
            className="btn btn-primary"
            onClick={() => setShowBulkCreate(true)}
          >
            + Create Payout
          </button>
          <button
            onClick={handlePayoutLogout}
            style={{ padding: '8px 16px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
          >
            🔓 Logout
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="payout-stats-grid" aria-live="polite" aria-label="Payout statistics">
        <div className="payout-stat-card">
          <div className="stat-label">Total Payouts</div>
          <div className="stat-value">{stats.totalPayouts}</div>
        </div>
        <div className="payout-stat-card">
          <div className="stat-label">Successful</div>
          <div className="stat-value success">{stats.successfulPayouts}</div>
        </div>
        <div className="payout-stat-card">
          <div className="stat-label">Pending</div>
          <div className="stat-value pending">{stats.pendingPayouts}</div>
        </div>
        <div className="payout-stat-card">
          <div className="stat-label">Total Amount</div>
          <div className="stat-value">{formatAmount(stats.totalAmount)}</div>
        </div>
        <div className="payout-stat-card">
          <div className="stat-label">Success Rate</div>
          <div className="stat-value">{stats.successRate.toFixed(1)}%</div>
        </div>
      </div>

      {/* Bank Services */}
      <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0 }}>🏦 Bank Services (ICICI CIB)</h3>
          {(bankServiceResult || statementFrom || statementTo || inquiryUniqueId) && (
            <button
              onClick={() => { setBankServiceResult(null); setStatementFrom(''); setStatementTo(''); setInquiryUniqueId(''); }}
              style={{ padding: '6px 14px', background: '#fff', color: '#dc3545', border: '1px solid #dc3545', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
              ✕ Clear
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <button onClick={checkBalance} disabled={bankServiceLoading}
            style={{ padding: '10px 20px', background: '#007a75', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
            {bankServiceLoading ? '...' : '💰 Check Balance'}
          </button>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>From</label>
              <input type="date" value={statementFrom} onChange={e => setStatementFrom(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>To</label>
              <input type="date" value={statementTo} onChange={e => setStatementTo(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }} />
            </div>
            <button onClick={getAccountStatement} disabled={bankServiceLoading}
              style={{ padding: '10px 20px', background: '#0056b3', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
              📄 Statement
            </button>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>UNIQUEID</label>
              <input type="text" value={inquiryUniqueId} onChange={e => setInquiryUniqueId(e.target.value)}
                placeholder="e.g. 1781589416101" style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px', width: '160px' }} />
            </div>
            <button onClick={transactionInquiry} disabled={bankServiceLoading}
              style={{ padding: '10px 20px', background: '#6f42c1', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
              🔍 Inquiry
            </button>
          </div>
        </div>
        {bankServiceResult && (
          <div style={{ marginTop: '16px', padding: '16px', background: bankServiceResult.error ? '#fff3f3' : '#f0fff4',
            border: `1px solid ${bankServiceResult.error ? '#ffcdd2' : '#b2dfdb'}`, borderRadius: '8px' }}>
            {bankServiceResult.error ? (
              <div style={{ color: '#c62828', fontWeight: '500' }}>❌ {bankServiceResult.error}</div>
            ) : bankServiceResult.type === 'balance' ? (
              <div>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Account Balance</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#1b5e20' }}>
                  ₹{bankServiceResult.data?.EFFECTIVEBAL ? Number(bankServiceResult.data.EFFECTIVEBAL).toLocaleString('en-IN') : '—'}
                </div>
                <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
                  Account: {bankServiceResult.data?.ACCOUNTNO || '—'} | Currency: {bankServiceResult.data?.CURRENCY || 'INR'} | Date: {bankServiceResult.data?.DATE || '—'}
                </div>
                {bankServiceResult.data?.RESPONSE === 'SUCCESS' && (
                  <span style={{ display: 'inline-block', marginTop: '8px', padding: '3px 10px', background: '#e8f5e9', color: '#2e7d32', borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}>✓ SUCCESS</span>
                )}
              </div>
            ) : bankServiceResult.type === 'inquiry' ? (
              <div>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Transaction Inquiry</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#888' }}>Status</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: bankServiceResult.data?.RESPONSE === 'SUCCESS' ? '#2e7d32' : '#c62828' }}>
                      {bankServiceResult.data?.STATUS || bankServiceResult.data?.RESPONSE || '—'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#888' }}>UTR Number</div>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>{bankServiceResult.data?.UTRNUMBER || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#888' }}>Unique ID</div>
                    <div style={{ fontSize: '14px' }}>{bankServiceResult.data?.UNIQUEID || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#888' }}>Response</div>
                    <span style={{ padding: '3px 10px', background: bankServiceResult.data?.RESPONSE === 'SUCCESS' ? '#e8f5e9' : '#ffebee', 
                      color: bankServiceResult.data?.RESPONSE === 'SUCCESS' ? '#2e7d32' : '#c62828', borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}>
                      {bankServiceResult.data?.RESPONSE || '—'}
                    </span>
                  </div>
                </div>
                {bankServiceResult.data?.MESSAGE && (
                  <div style={{ marginTop: '8px', padding: '8px', background: '#fff3e0', borderRadius: '4px', fontSize: '13px' }}>
                    Message: {bankServiceResult.data.MESSAGE}
                  </div>
                )}
              </div>
            ) : bankServiceResult.type === 'statement' ? (
              <div>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Account Statement</div>
                {bankServiceResult.data?.RESPONSE === 'Failure' || bankServiceResult.data?.RESPONSE === 'FAILURE' ? (
                  <div style={{ color: '#c62828' }}>No statement data available. {bankServiceResult.data?.MESSAGE || ''}</div>
                ) : bankServiceResult.data?.Record ? (
                  <div>
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                      Account: {bankServiceResult.data.ACCOUNTNO} | Records: {bankServiceResult.data.Record.length}
                    </div>
                    <div style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead>
                          <tr style={{ background: '#f5f5f5', position: 'sticky', top: 0 }}>
                            <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Date</th>
                            <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Remarks</th>
                            <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Amount</th>
                            <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Type</th>
                            <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Balance</th>
                            <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Txn ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bankServiceResult.data.Record.map((r, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{r.TXNDATE?.split(' ')[0] || r.VALUEDATE}</td>
                              <td style={{ padding: '6px 8px', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.REMARKS}>{r.REMARKS}</td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '600', color: r.TYPE === 'CR' ? '#2e7d32' : '#c62828' }}>
                                {r.TYPE === 'CR' ? '+' : '-'}₹{r.AMOUNT}
                              </td>
                              <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                                <span style={{ padding: '2px 6px', borderRadius: '3px', fontSize: '11px', fontWeight: '600',
                                  background: r.TYPE === 'CR' ? '#e8f5e9' : '#ffebee', color: r.TYPE === 'CR' ? '#2e7d32' : '#c62828' }}>
                                  {r.TYPE}
                                </span>
                              </td>
                              <td style={{ padding: '6px 8px', textAlign: 'right' }}>₹{r.BALANCE}</td>
                              <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '11px' }}>{r.TRANSACTIONID}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <pre style={{ margin: 0, fontSize: '13px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {JSON.stringify(bankServiceResult.data, null, 2)}
                  </pre>
                )}
              </div>
            ) : (
              <pre style={{ margin: 0, fontSize: '13px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {JSON.stringify(bankServiceResult.data, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="payout-filters-card">
        <h2>Filter Payouts</h2>
        <div className="payout-filters-grid">
          <div className="filter-group">
            <label htmlFor="filter-status">Status</label>
            <select
              id="filter-status"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="form-control"
            >
              <option value="">All Status</option>
              <option value="PENDING_APPROVAL">Pending Approval</option>
              <option value="APPROVED">Approved</option>
              <option value="INITIATED">Initiated</option>
              <option value="PROCESSING">Awaiting CIB Portal Approval</option>
              <option value="SUCCESS">Success</option>
              <option value="FAILED">Failed</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="filter-date-from">From Date</label>
            <input
              id="filter-date-from"
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              max={new Date().toISOString().split('T')[0]}
              className="form-control"
            />
          </div>
          <div className="filter-group">
            <label htmlFor="filter-date-to">To Date</label>
            <input
              id="filter-date-to"
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              max={new Date().toISOString().split('T')[0]}
              className={`form-control${filters.dateTo && filters.dateFrom && filters.dateTo < filters.dateFrom ? ' is-invalid' : ''}`}
            />
            {filters.dateTo && filters.dateFrom && filters.dateTo < filters.dateFrom && (
              <span style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                To date cannot be earlier than From date
              </span>
            )}
          </div>
          <div className="filter-group">
            <label htmlFor="filter-min-amount">Min Amount</label>
            <input
              id="filter-min-amount"
              type="number"
              value={filters.minAmount}
              onChange={(e) => { const val = e.target.value; if (val === '' || Number(val) >= 0) setFilters({ ...filters, minAmount: val }); }}
              placeholder="0"
              min="0"
              className="form-control"
            />
          </div>
          <div className="filter-group">
            <label htmlFor="filter-max-amount">Max Amount</label>
            <input
              id="filter-max-amount"
              type="number"
              value={filters.maxAmount}
              onChange={(e) => { const val = e.target.value; if (val === '' || Number(val) >= 0) setFilters({ ...filters, maxAmount: val }); }}
              placeholder="No limit"
              min="0"
              className="form-control"
            />
          </div>
          <div className="filter-group">
            <button className="btn btn-secondary" onClick={clearFilters} aria-label="Clear all payout filters">
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Payouts Table */}
      <div className="payout-table-card">
        <h2>Payout History ({totalPayoutCount})</h2>
        {payouts.length > 0 ? (
          <div className="payout-table-wrapper">
            <table className="payout-table" role="table">
              <caption className="sr-only">Payout Transactions</caption>
              <thead>
                <tr>
                  <th scope="col">Reference ID</th>
                  <th scope="col">Bank Ref (UNIQUEID)</th>
                  <th scope="col">Beneficiary</th>
                  <th scope="col">Amount</th>
                  <th scope="col">Status</th>
                  <th scope="col">UTR</th>
                  <th scope="col">Created At</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {payoutPagination.pageData.map((payout) => (
                  <tr key={payout.payoutId}>
                    <td className="ref-id">{payout.referenceId}</td>
                    <td style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                      {payout.providerReference ? (
                        <span style={{ cursor: 'pointer', color: '#007a75', textDecoration: 'underline' }}
                          onClick={() => setInquiryUniqueId(payout.providerReference)}
                          title="Click to use in Inquiry">
                          {payout.providerReference}
                        </span>
                      ) : '—'}
                    </td>
                    <td>
                      {payout.beneficiaryName || (payout.beneficiaryAccount ? `****${payout.beneficiaryAccount.slice(-4)}` : payout.beneficiaryVpa || 'N/A')}
                    </td>
                    <td className="amount">{formatAmount(payout.amount)}</td>
                    <td>{getStatusBadge(payout.status)}</td>
                    <td style={{ fontSize: '12px', fontFamily: 'monospace' }}>{payout.providerTxnId || '—'}</td>
                    <td>{new Date(payout.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-info"
                        onClick={() => fetchPayoutDetails(payout.payoutId)  }
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination {...payoutPagination} pageSize={10} />
          </div>
        ) : (
          <div className="payout-empty-state">
            <p>No payouts found matching the current filters.</p>
            <button
              className="btn btn-primary"
              onClick={() => setShowBulkCreate(true)}
            >
              Create Your First Payout
            </button>
          </div>
        )}
      </div>

      {/* Create Payout Modal */}
      {showBulkCreate && (
        <BulkPayoutCreator
          payoutToken={payoutToken}
          onClose={() => setShowBulkCreate(false)}
          onSuccess={(result) => {
            showNotification(`Payout complete: ${result.successCount} created successfully`);
            fetchPayouts();
            fetchStats();
          }}
        />
      )}

      {/* Payout Details Modal */}
      {selectedPayoutId && (
        <PayoutDetailsModal
          payoutDetails={payoutDetails}
          loading={detailsLoading}
          onClose={closeDetailsModal}
        />
      )}
    </div>
  );
};

// Create Payout Modal Component
const CreatePayoutModal = ({ onClose, onSuccess, payoutToken }) => {
  const modalRef = useFocusTrap(true);
  const [idempotencyKey, setIdempotencyKey] = useState(() => `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const [formData, setFormData] = useState({
    paymentMode: 'IMPS',
    beneficiaryVpa: '',
    beneficiaryAccount: '',
    ifscCode: '',
    beneficiaryName: '',
    beneficiaryMobile: '',
    senderName: '',
    amount: '',
    payoutType: 'CASHBACK',
    remarks: '',
    purpose: '',
    saveBeneficiary: true
  });
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [savedBeneficiaries, setSavedBeneficiaries] = useState([]);
  const [dupWarning, setDupWarning]   = useState(null); // { name, id }
  const [deleteConfirm, setDeleteConfirm] = useState(null); // beneficiary id

  // Fetch saved beneficiaries on mount
  useEffect(() => {
    axios.get('/api/v1/payouts/beneficiaries', {
      headers: payoutToken ? { Authorization: `Bearer ${payoutToken}` } : {},
      _skipAuthInterceptor: true
    }).then(res => {
      const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setSavedBeneficiaries(list.filter(b => b.savedByUser));
    }).catch(() => {});
  }, [payoutToken]);

  // Check for duplicate when account + IFSC are filled
  useEffect(() => {
    if (formData.paymentMode !== 'IMPS') { setDupWarning(null); return; }
    if (!formData.beneficiaryAccount || !formData.ifscCode) { setDupWarning(null); return; }
    const dup = savedBeneficiaries.find(
      b => b.accountNumber === formData.beneficiaryAccount && b.ifscCode === formData.ifscCode
    );
    setDupWarning(dup || null);
  }, [formData.beneficiaryAccount, formData.ifscCode, formData.paymentMode, savedBeneficiaries]);

  const fillFromSaved = (b) => {
    setFormData(prev => ({
      ...prev,
      beneficiaryAccount: b.accountNumber || '',
      ifscCode: b.ifscCode || '',
      beneficiaryName: b.name || b.beneficiaryName || '',
      beneficiaryMobile: b.mobile || b.beneficiaryMobile || '',
    }));
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/v1/payouts/beneficiaries/${id}`, {
        headers: payoutToken ? { Authorization: `Bearer ${payoutToken}` } : {}
      });
      setSavedBeneficiaries(prev => prev.filter(b => b.id !== id));
    } catch {
      // ignore
    }
    setDeleteConfirm(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Build request based on payment mode
      const requestData = {
        paymentMode: formData.paymentMode,
        amount: parseFloat(formData.amount),
        payoutType: formData.payoutType,
        remarks: formData.remarks,
        purpose: formData.purpose,
        saveBeneficiary: formData.saveBeneficiary,
        idempotencyKey  // same key for this form session — retries won't create duplicates
      };

      if (formData.paymentMode === 'IMPS') {
        requestData.beneficiaryAccount = formData.beneficiaryAccount;
        requestData.ifscCode = formData.ifscCode;
        requestData.beneficiaryName = formData.beneficiaryName;
        requestData.beneficiaryMobile = formData.beneficiaryMobile;
        requestData.senderName = formData.senderName;
      } else {
        requestData.beneficiaryVpa = formData.beneficiaryVpa;
      }

      console.log('Sending payout request:', requestData);
      
      const response = await axios.post('/api/v1/payouts', requestData, {
        headers: payoutToken ? { Authorization: `Bearer ${payoutToken}` } : {},
        _skipAuthInterceptor: true
      });

      // Backend returns PayoutResult directly (not wrapped in success/data)
      if (response.data && response.data.payoutId) {
        const result = response.data;
        onSuccess(result);
        // Reset form with a new UUID so user can create another payout immediately without closing
        setIdempotencyKey(`${Date.now()}-${Math.random().toString(36).slice(2)}`);
        setFormData({
          paymentMode: 'IMPS',
          beneficiaryVpa: '',
          beneficiaryAccount: '',
          ifscCode: '',
          beneficiaryName: '',
          beneficiaryMobile: '',
          senderName: '',
          amount: '',
          payoutType: 'CASHBACK',
          remarks: '',
          purpose: '',
          saveBeneficiary: true
        });
        setError('');
      } else {
        setError('Unexpected response format from server');
      }
    } catch (error) {
      console.error('Error creating payout:', error);
      console.error('Error response:', error.response?.data);
      
      // Extract detailed error message
      let errorMessage = 'Failed to create payout';
      if (error.response?.data) {
        if (error.response.data.message) {
          errorMessage = error.response.data.message;
          
          // Make IMPS verification message more user-friendly
          if (errorMessage.includes('ICICI Bank IMPS does not support separate beneficiary verification')) {
            errorMessage = 'Note: IMPS beneficiary verification will happen during payout processing. Your payout has been created and is pending approval.';
          }
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getTransactionLimits = () => {
    if (formData.paymentMode === 'IMPS') {
      return { min: 1, max: 500000 };
    }
    return { min: 1, max: 100000 }; // UPI limits
  };

  const limits = getTransactionLimits();

  return (
    <div className="modal-overlay" role="presentation" onKeyDown={(e) => e.key === 'Escape' && onClose()}>
      <div ref={modalRef} className="modal-content payout-modal" role="dialog" aria-modal="true" aria-labelledby="create-payout-title">
        <div className="modal-header">
          <h3 id="create-payout-title">Create New Payout</h3>
          <button className="modal-close" onClick={onClose} aria-label="Close payout creation dialog">&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          {/* Saved Beneficiaries */}
          {savedBeneficiaries.length > 0 && (
            <div style={{ marginBottom: '16px', padding: '12px', background: '#f0f7ff', borderRadius: '8px', border: '1px solid #b8daff' }}>
              <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '8px', color: '#004085' }}>📋 Saved Beneficiaries</div>
              {savedBeneficiaries.map(b => (
                <div key={b.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', marginBottom: '4px', background: '#fff', borderRadius: '6px', border: '1px solid #dee2e6', fontSize: '13px' }}>
                  <button type="button" onClick={() => fillFromSaved(b)}
                    style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', color: '#034659', fontWeight: '500' }}>
                    {b.name || b.beneficiaryName} — ****{(b.accountNumber || '').slice(-4)} | {b.ifscCode}
                  </button>
                  {deleteConfirm === b.id ? (
                    <span style={{ display: 'flex', gap: '4px' }}>
                      <button type="button" onClick={() => handleDelete(b.id)}
                        style={{ padding: '2px 8px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Confirm</button>
                      <button type="button" onClick={() => setDeleteConfirm(null)}
                        style={{ padding: '2px 8px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
                    </span>
                  ) : (
                    <button type="button" onClick={() => setDeleteConfirm(b.id)}
                      style={{ padding: '2px 8px', background: 'none', border: '1px solid #dc3545', color: '#dc3545', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>🗑</button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Duplicate account warning */}
          {dupWarning && (
            <div style={{ marginBottom: '12px', padding: '10px 14px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', fontSize: '13px', color: '#856404' }}>
              ⚠️ This account + IFSC is already saved as <strong>{dupWarning.name || dupWarning.beneficiaryName}</strong>. Use it from the saved list above instead of saving again.
            </div>
          )}
          <div className="form-group">
            <label>Payment Mode *</label>
            <select
              value={formData.paymentMode}
              onChange={(e) => setFormData({ 
                ...formData, 
                paymentMode: e.target.value,
                // Clear fields when switching modes
                beneficiaryVpa: '',
                beneficiaryAccount: '',
                ifscCode: '',
                beneficiaryName: '',
                beneficiaryMobile: '',
                senderName: ''
              })}
              className="form-control"
              required
            >
              <option value="UPI">UPI</option>
              <option value="IMPS">IMPS (Instant Bank Transfer)</option>
            </select>
            <small className="form-text">
              {formData.paymentMode === 'IMPS' 
                ? `IMPS: ₹${limits.min} - ₹${limits.max.toLocaleString('en-IN')} per transaction (24x7 available)`
                : `UPI: ₹${limits.min} - ₹${limits.max.toLocaleString('en-IN')} per transaction`
              }
            </small>
          </div>

          {formData.paymentMode === 'UPI' ? (
            <div className="form-group">
              <label htmlFor="beneficiary-vpa">Beneficiary VPA *</label>
              <input
                id="beneficiary-vpa"
                type="text"
                value={formData.beneficiaryVpa}
                onChange={(e) => setFormData({ ...formData, beneficiaryVpa: e.target.value })}
                placeholder="user@bank"
                className="form-control"
                required aria-required="true"
              />
              <small className="form-text">Enter UPI ID (e.g., user@paytm, 9876543210@ybl)</small>
            </div>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="beneficiary-account">Beneficiary Account Number *</label>
                <input
                  id="beneficiary-account"
                  type="text"
                  value={formData.beneficiaryAccount}
                  onChange={(e) => setFormData({ ...formData, beneficiaryAccount: e.target.value })}
                  placeholder="12345678901234"
                  className="form-control"
                  pattern="\d{7,18}"
                  required aria-required="true"
                />
                <small className="form-text">7-18 digit account number</small>
              </div>
              <div className="form-group">
                <label htmlFor="ifsc-code">IFSC Code *</label>
                <input
                  id="ifsc-code"
                  type="text"
                  value={formData.ifscCode}
                  onChange={(e) => setFormData({ ...formData, ifscCode: e.target.value.toUpperCase() })}
                  placeholder="ICIC0001234"
                  className="form-control"
                  pattern="[A-Z]{4}0[A-Z0-9]{6}"
                  maxLength="11"
                  required aria-required="true"
                />
                <small className="form-text">11-character IFSC code (e.g., ICIC0001234)</small>
              </div>
              <div className="form-group">
                <label htmlFor="beneficiary-name">Beneficiary Name *</label>
                <input
                  id="beneficiary-name"
                  type="text"
                  value={formData.beneficiaryName}
                  onChange={(e) => setFormData({ ...formData, beneficiaryName: e.target.value })}
                  placeholder="John Doe"
                  className="form-control"
                  required aria-required="true"
                />
                <small className="form-text">Name as per bank account</small>
              </div>
              <div className="form-group">
                <label htmlFor="beneficiary-mobile">Beneficiary Mobile (Optional)</label>
                <input
                  id="beneficiary-mobile"
                  type="text"
                  value={formData.beneficiaryMobile}
                  onChange={(e) => setFormData({ ...formData, beneficiaryMobile: e.target.value })}
                  placeholder="9876543210"
                  className="form-control"
                  pattern="\d{10}"
                  maxLength="10"
                />
                <small className="form-text">10-digit mobile number</small>
              </div>
              <div className="form-group">
                <label htmlFor="sender-name">Sender Name (Optional)</label>
                <input
                  id="sender-name"
                  type="text"
                  value={formData.senderName}
                  onChange={(e) => setFormData({ ...formData, senderName: e.target.value })}
                  placeholder="Your Company Name"
                  className="form-control"
                />
                <small className="form-text">Sender name as per your account</small>
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  id="save-beneficiary"
                  type="checkbox"
                  checked={formData.saveBeneficiary}
                  onChange={(e) => setFormData({ ...formData, saveBeneficiary: e.target.checked })}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <label htmlFor="save-beneficiary" style={{ margin: 0, cursor: 'pointer', fontWeight: '500' }}>
                  Save beneficiary for future payouts
                </label>
              </div>
            </>
          )}
          <div className="form-group">
            <label htmlFor="payout-amount">Amount (INR) *</label>
            <input
              id="payout-amount"
              type="text"
              value={formData.amount}
              // onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              onChange={(e) => {const value = e.target.value;
              if (/^\d*\.?\d{0,2}$/.test(value)) {
              setFormData({ ...formData, amount: value });
              }
              }}
              placeholder="1000.00"
              className="form-control"
              min={limits.min}
              max={limits.max}
              // step="0.01"
              required aria-required="true"
            />
            <small className="form-text">
              Min: ₹{limits.min}, Max: ₹{limits.max.toLocaleString('en-IN')}
            </small>
          </div>
          <div className="form-group">
            <label htmlFor="payout-type">Payout Type *</label>
            <select
              id="payout-type"
              value={formData.payoutType}
              onChange={(e) => setFormData({ ...formData, payoutType: e.target.value })}
              className="form-control"
              required aria-required="true"
            >
              <option value="REFUND">Refund</option>
              <option value="CASHBACK">Cashback</option>
              <option value="REWARD">Reward</option>
              <option value="VENDOR_PAYMENT">Vendor Payment</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="payout-purpose">Purpose</label>
            <input
              id="payout-purpose"
              type="text"
              value={formData.purpose}
              onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
              placeholder="Payment purpose"
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label htmlFor="payout-remarks">Remarks</label>
            <textarea
              id="payout-remarks"
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              placeholder="Additional remarks"
              className="form-control"
              rows="3"
            />
          </div>
          {error && <div className="alert alert-danger" role="alert" aria-live="assertive">{error}</div>}
          {/* Save beneficiary checkbox — bottom right of form */}
          {formData.paymentMode === 'IMPS' && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <input
                id="save-beneficiary"
                type="checkbox"
                checked={formData.saveBeneficiary}
                onChange={(e) => setFormData({ ...formData, saveBeneficiary: e.target.checked })}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <label htmlFor="save-beneficiary" style={{ margin: 0, cursor: 'pointer', fontWeight: '500', fontSize: '13px' }}>
                Save beneficiary for future payouts
              </label>
            </div>
          )}
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Payout'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Payout Details Modal Component
const PayoutDetailsModal = ({ payoutDetails, loading, onClose }) => {
  const modalRef = useFocusTrap(true);
  if (loading || !payoutDetails) {
    return (
      <div className="modal-overlay" role="presentation">
        <div ref={modalRef} className="modal-content payout-modal" role="dialog" aria-modal="true" aria-labelledby="payout-details-loading-title">
          <div className="modal-header">
            <h3 id="payout-details-loading-title">Payout Details</h3>
            <button className="modal-close" onClick={onClose} aria-label="Close payout details dialog">&times;</button>
          </div>
          <div style={{ padding: '40px', textAlign: 'center' }} role="status" aria-live="polite">
            Loading payout details...
          </div>
        </div>
      </div>
    );
  }

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusClass = 
      status === 'SUCCESS' ? 'status-success' :
      status === 'PENDING_APPROVAL' ? 'status-warning' :
      status === 'PENDING_BANK_APPROVAL' ? 'status-warning' :
      status === 'APPROVED' || status === 'INITIATED' || status === 'PROCESSING' ? 'status-pending' :
      status === 'FAILED' ? 'status-failed' :
      status === 'REJECTED' ? 'status-danger' : 'status-secondary';

    const label = status === 'PROCESSING' ? 'Awaiting CIB Portal Approval' : status;
    return <span className={`status-badge ${statusClass}`}>{label}</span>;
  };

  return (
    <div className="modal-overlay" role="presentation">
      <div ref={modalRef} className="modal-content payout-modal" style={{ maxWidth: '800px' }} role="dialog" aria-modal="true" aria-labelledby="payout-details-title">
        <div className="modal-header">
          <h3 id="payout-details-title">💸 Payout Details</h3>
          <button className="modal-close" onClick={onClose} aria-label="Close payout details dialog">&times;</button>
        </div>
        
        <div style={{ padding: '20px' }}>
          {/* Basic Information */}
          <div className="details-section">
            <h4>Basic Information</h4>
            <div className="details-grid">
              <div className="detail-item">
                <span className="detail-label">Reference ID:</span>
                <span className="detail-value">{payoutDetails.referenceId}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Status:</span>
                <span className="detail-value">{getStatusBadge(payoutDetails.status)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Amount:</span>
                <span className="detail-value" style={{ fontWeight: 'bold', color: '#28a745' }}>
                  {formatAmount(payoutDetails.amount)}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Type:</span>
                <span className="detail-value">{payoutDetails.payoutType}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Bank Provider:</span>
                <span className="detail-value">{payoutDetails.bankProvider}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Created At:</span>
                <span className="detail-value">
                  {new Date(payoutDetails.createdAt + 'Z').toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                </span>
              </div>
            </div>
          </div>

          {/* Beneficiary Information */}
          {payoutDetails.beneficiaryVpa && (
            <div className="details-section">
              <h4>Beneficiary Information</h4>
              <div className="details-grid">
                {payoutDetails.bankProvider === 'ICICI_IMPS' ? (
                  <>
                    <div className="detail-item">
                      <span className="detail-label">Account Number:</span>
                      <span className="detail-value">
                        {payoutDetails.beneficiaryVpa && payoutDetails.beneficiaryVpa.includes('|') 
                          ? '****' + payoutDetails.beneficiaryVpa.split('|')[0].slice(-4)
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">IFSC Code:</span>
                      <span className="detail-value">
                        {payoutDetails.beneficiaryVpa && payoutDetails.beneficiaryVpa.includes('|') 
                          ? payoutDetails.beneficiaryVpa.split('|')[1]
                          : 'N/A'}
                      </span>
                    </div>
                    {payoutDetails.impsBeneName && (
                      <div className="detail-item">
                        <span className="detail-label">Beneficiary Name:</span>
                        <span className="detail-value">{payoutDetails.impsBeneName}</span>
                      </div>
                    )}
                    {payoutDetails.impsBeneMobile && (
                      <div className="detail-item">
                        <span className="detail-label">Mobile:</span>
                        <span className="detail-value">{payoutDetails.impsBeneMobile}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="detail-item">
                      <span className="detail-label">VPA:</span>
                      <span className="detail-value">{payoutDetails.beneficiaryVpa}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Name:</span>
                      <span className="detail-value">{payoutDetails.beneficiaryName || 'N/A'}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* IMPS Transaction Details */}
          {payoutDetails.bankProvider === 'ICICI_IMPS' && (payoutDetails.impsBankRrn || payoutDetails.impsTranRefNo) && (
            <div className="details-section">
              <h4>IMPS Transaction Details</h4>
              <div className="details-grid">
                {payoutDetails.impsBankRrn && (
                  <div className="detail-item">
                    <span className="detail-label">Bank RRN:</span>
                    <span className="detail-value" style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                      {payoutDetails.impsBankRrn}
                    </span>
                  </div>
                )}
                {payoutDetails.impsTranRefNo && (
                  <div className="detail-item">
                    <span className="detail-label">Transaction Ref No:</span>
                    <span className="detail-value" style={{ fontFamily: 'monospace' }}>
                      {payoutDetails.impsTranRefNo}
                    </span>
                  </div>
                )}
                {payoutDetails.senderName && (
                  <div className="detail-item">
                    <span className="detail-label">Sender Name:</span>
                    <span className="detail-value">{payoutDetails.senderName}</span>
                  </div>
                )}
                {payoutDetails.localTxnDtTime && (
                  <div className="detail-item">
                    <span className="detail-label">Transaction Time:</span>
                    <span className="detail-value">
                      {new Date(payoutDetails.localTxnDtTime).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Additional Details */}
          <div className="details-section">
            <h4>Additional Details</h4>
            <div className="details-grid">
              {payoutDetails.purpose && (
                <div className="detail-item">
                  <span className="detail-label">Purpose:</span>
                  <span className="detail-value">{payoutDetails.purpose}</span>
                </div>
              )}
              {payoutDetails.remarks && (
                <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                  <span className="detail-label">Remarks:</span>
                  <span className="detail-value">{payoutDetails.remarks}</span>
                </div>
              )}
              {payoutDetails.initiatedBy && (
                <div className="detail-item">
                  <span className="detail-label">Initiated By:</span>
                  <span className="detail-value">{payoutDetails.initiatedBy}</span>
                </div>
              )}
              {payoutDetails.updatedAt && (
                <div className="detail-item">
                  <span className="detail-label">Last Updated:</span>
                  <span className="detail-value">
                    {new Date(payoutDetails.updatedAt).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Status History */}
          {payoutDetails.statusHistory && payoutDetails.statusHistory.length > 0 && (
            <div className="details-section">
              <h4>Status History</h4>
              <div className="status-timeline">
                {payoutDetails.statusHistory.map((history, index) => (
                  <div key={index} className="timeline-item">
                    <div className="timeline-marker"></div>
                    <div className="timeline-content">
                      <div className="timeline-status">{getStatusBadge(history.status)}</div>
                      <div className="timeline-date">
                        {new Date(history.timestamp).toLocaleString()}
                      </div>
                      {history.remarks && (
                        <div className="timeline-remarks">{history.remarks}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MerchantPayoutDashboard;
